import { Col, Row, Sidepanel, Text } from 'components';
import { formatLocale, theme } from 'lib';
import React from 'react';
import ReactPlayer from 'react-player';
import { useSelector } from 'react-redux';
import { ContentHeader } from 'screens/Admin/components';
import styled from 'styled-components';

const Section = styled.div`
  padding: 20px 24px 0;
`;

const MediaFrame = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: ${theme.GREY_10};
  border: 1px solid ${theme.GREY_20};
`;

const PreviewImage = styled.img`
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  display: block;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => (p.live ? '#FFF3E0' : '#E8F5E9')};
  color: ${(p) => (p.live ? '#E65100' : '#2E7D32')};
`;

const TagChip = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  background: ${theme.GREY_10};
  color: ${theme.GREY_70};
  margin: 0 4px 4px 0;
`;

const DetailRow = ({ label, value }) => (
  <Col noFlex marg="0 0 16px">
    <Text c1 med col={theme.GREY_60} marg="0 0 4px">
      {label}
    </Text>
    <Text>{value || '-'}</Text>
  </Col>
);

const FancamPanel = ({ dataId, closeModal }) => {
  const fancam = useSelector((state) => state.admin?.fancams?.find((x) => x._id === dataId));
  const isVideo = fancam?.media?.type === 'video';
  const isLive = fancam?.type === 'live';

  return (
    <Sidepanel wid={640} pad="0" visible={dataId} closeModal={closeModal}>
      <ContentHeader
        text={
          !fancam
            ? 'Fancam details'
            : `${fancam?.owner?.name || fancam?.owner?.username || 'Unknown'}'s fancam`
        }
        closePanel={closeModal}
      />

      {fancam && (
        <React.Fragment>
          {fancam.media?.url && (
            <Section>
              <Text c1 med col={theme.GREY_60} marg="0 0 8px">
                Media
              </Text>
              <MediaFrame>
                {isVideo ? (
                  <ReactPlayer url={fancam.media.url} controls width="100%" height="360px" />
                ) : (
                  <PreviewImage src={fancam.media.url} alt="fancam media" />
                )}
              </MediaFrame>
            </Section>
          )}

          <Section>
            <Row gap="24px" start>
              <Col noFlex wid="50%">
                <DetailRow
                  label="Owner"
                  value={
                    fancam.owner?.name
                      ? `${fancam.owner.name}${fancam.owner.username ? ` (@${fancam.owner.username})` : ''}`
                      : fancam.owner?.username
                  }
                />
                <DetailRow label="Team" value={fancam.team?.name || '-'} />
                <DetailRow
                  label="Posted At"
                  value={
                    fancam.createdAt
                      ? formatLocale(new Date(fancam.createdAt), 'LLL do yyy, hh:mm a')
                      : '-'
                  }
                />
              </Col>

              <Col noFlex wid="50%">
                <Col noFlex marg="0 0 16px">
                  <Text c1 med col={theme.GREY_60} marg="0 0 4px">
                    Type
                  </Text>
                  <TypeBadge live={isLive}>{isLive ? 'Live' : 'Challenge'}</TypeBadge>
                </Col>
                <DetailRow label="Views" value={String(fancam.viewCount ?? 0)} />
                <DetailRow label="Reactions" value={String(fancam.reactionCount ?? 0)} />
                <DetailRow label="Comments" value={String(fancam.commentsCount ?? 0)} />
              </Col>
            </Row>
          </Section>

          {fancam.caption && (
            <Section>
              <Text c1 med col={theme.GREY_60} marg="0 0 8px">
                Caption
              </Text>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{fancam.caption}</Text>
            </Section>
          )}

          {fancam.tags?.length > 0 && (
            <Section>
              <Text c1 med col={theme.GREY_60} marg="0 0 8px">
                Tags
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {fancam.tags.map((tag) => (
                  <TagChip key={tag}>#{tag}</TagChip>
                ))}
              </div>
            </Section>
          )}
        </React.Fragment>
      )}
    </Sidepanel>
  );
};

export default FancamPanel;
